import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CreatedCampaignRecord,
  CreateCampaignRequest,
  CreateCampaignResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { CAMPAIGNS_LIST_QUERY_KEY } from "./campaignQueryCache";
import { getGamePassConfigQueryOptions } from "./useGetGamePassConfig";
import { unwrapGamePassEntity } from "./responseMappers";

/**
 * Create a Monthly Challenge campaign (`POST /gamepass/v2/admin/campaigns`).
 *
 * Mirrors `useCreateSeason`: `operator_id` is not authored in the wizard, so it
 * is pulled from `GET /config` and appended here. The `{ success, data }`
 * envelope is unwrapped to the created record.
 */
const postCreateCampaign = async (
  payload: CreateCampaignRequest,
  operatorId: string,
): Promise<CreatedCampaignRecord> => {
  const response = await getPrivateInstance().post<CreateCampaignResponse>(
    API_ROUTES.CREATE_CAMPAIGN,
    {
      ...payload,
      operator_id: operatorId,
    },
  );
  return unwrapGamePassEntity(response.data, "campaign");
};

export const useCreateCampaign = (): UseMutationResult<
  CreatedCampaignRecord,
  Error,
  CreateCampaignRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaigns", "create"],
    mutationFn: async (payload) => {
      const config = await queryClient.fetchQuery(
        getGamePassConfigQueryOptions(),
      );
      return postCreateCampaign(payload, config.operator_id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CAMPAIGNS_LIST_QUERY_KEY });
    },
  });
};
